import { Component, effect, OnDestroy, OnInit, ViewChild, AfterViewInit } from '@angular/core';
import { Table } from 'primeng/table';
import { Popover } from 'primeng/popover';
import { Subscription } from 'rxjs/internal/Subscription';
import { ColumnName, ItemState } from 'src/app/shared/models/core';
import { DataRepositoryService } from 'src/app/shared/services/data-repository/data-repository.service';
import { TaskInfoService } from 'src/app/shared/services/task-info/task-info.service';
import { TaskbankColumnService } from '../column-service/taskbank-column.service';
import { DataAccessService } from 'src/app/shared/services/data-access/data-access.service';
import { TaskMovementService } from 'src/app/shared/services/operations/task-movement.service';
import { Column } from 'src/app/modules/gantt/component/gantt/gantt.component';
import { IUPPTasksMap } from 'src/app/shared/models/task-unplannedpickup';
import { Navigation } from 'src/app/shared/entity/navigation';
import { TBTabName } from 'src/app/shared/entity/enums';
import { SortingService } from 'src/app/shared/services/operations/sorting.service';
import { SelectedPreferencesENUM } from 'src/app/shared/models/preference';
import { KeyValue } from '@angular/common';
import { SelectedTBRowKPI } from 'src/app/shared/entity/utility-interfaces';
import { FilterService } from 'primeng/api';
import { getEventsByTab, getServiceCodesByTab, IndicatorDef } from 'src/app/shared/entity/indicator.const';
import { TaskBankFilterStateService } from 'src/app/shared/services/task-bank-filter/task-bank-filter-state.service';

@Component({
    selector: 'app-push-to-cloud',
    templateUrl: './push-to-cloud.component.html',
    styleUrls: ['../../../../../shared/css/primeng-table.component.scss'],
    standalone: false
})
export class PushToCloudComponent implements OnDestroy, OnInit, AfterViewInit  {
  @ViewChild('dataTable') table: Table;
  @ViewChild('indicatorPanel') indicatorPanel: Popover;
  visibleColumns: any[] = []
  showTable: boolean = true;
  private currentPreferenceId: string = '';
  private readonly taskFilterField = 'task_filter_keys';
  private readonly taskFilterKeys = Array.from(
    new Set([
      ...getServiceCodesByTab(TBTabName.PushToCloud).map((item) => item.key),
      ...getEventsByTab(TBTabName.PushToCloud).map((item) => item.key),
    ])
  );
  cloudData: IUPPTasksMap[] = [];
  taskBankSubscription: Subscription[] = [];
  itemState: ItemState;
  visible = false;
  itemTitle = '';
  itemColumnNames: Column[] = [];
  taskid: string = '';
  selectedIds: Set<string> = new Set();
  navigation = Navigation;
  activeIndicators: IndicatorDef[] = [];
  selectedTaskFilterServiceCode: string | null = null;
  selectedTaskFilterEvent: string | null = null;

  /**
   * Displays the indicator panel for a specific row, showing the active indicators
   * @param event The event that triggered the panel display
   * @param row The row data containing the indicators
   */
  showIndicatorPanel(event: Event, row: any): void {
    this.activeIndicators = row.indicators ?? [];
    this.indicatorPanel.toggle(event);
  }
  

  //getter and setter 
  get isCustomSort(): boolean{
    return this.sortingService.isCustomSort?? false;
  }
  // getter for select all value
  get isSelectAll() {
    return this.taskMovementService.isSelectedAll;
  }

  get showSelectedTaskKPI(): KeyValue<keyof SelectedTBRowKPI, string| number>[]{
    return this.taskMovementService.selectedTaskKPI;
  }

  constructor(public taskInfoService: TaskInfoService
    , public dataAccessService: DataAccessService
    , public dataRepositoryService: DataRepositoryService
    , private readonly taskBankColumnService: TaskbankColumnService
    , public taskMovementService: TaskMovementService
    , public readonly sortingService: SortingService
    , private readonly filterService: FilterService
    , private readonly taskBankFilterStateService: TaskBankFilterStateService
  ) {
    this.filterService.register('task-filter-any', (rowValue: string[] | undefined, filterValue: string[] | null) => {
      if (!filterValue?.length) return true;
      if (!Array.isArray(rowValue) || !rowValue.length) return false;
      return filterValue.some((key) => rowValue.includes(key));
    });
    this.sortedTaskSelectionEffect();
    this.selectedColumnEffect();
    this.taskBankFilterSignalEffect();
  }

  ngOnInit(): void {
    this.cloudSelectedSubs();
    this.polygonTaskSelectSubs$();
    this.polygonTaskDeselectSubs$();
  }

  ngAfterViewInit(): void {
    this.preserveTableScrollOnRefresh();
    this.applyTaskBankSelectionFilter();
  }

  taskBankFilterSignalEffect(): void {
    effect(() => {
      const taskFilterState = this.taskBankFilterStateService.taskBankFilterState();
      const isPushToCloudTab = this.taskInfoService.selectedTabName?.toLowerCase() === TBTabName.PushToCloud.toLowerCase();

      if (!isPushToCloudTab) {
        this.selectedTaskFilterServiceCode = null;
        this.selectedTaskFilterEvent = null;
        this.applyTaskBankSelectionFilter();
        return;
      }

      this.selectedTaskFilterServiceCode = taskFilterState.serviceCodeKey;
      this.selectedTaskFilterEvent = taskFilterState.eventKey;
      this.applyTaskBankSelectionFilter();
    });
  }

  /**
   * Method handles subscription for push to cloud task collection - full list and delta version
   */
  cloudSelectedSubs(){
    this.taskBankSubscription.push(
      this.dataAccessService.isCloudSelected$.subscribe((cloudData) => {
        if (cloudData) {
          //check if custom sort is applied on the table
          if (this.sortingService.isCustomSortApplied && this.dataRepositoryService.cloudTbCollection.size) {
            if (cloudData?.isDeltaUpdate) {
              this.setCloudData(this.sortingService.processCustomSortingWithDelta());
            }
            this.sortingService.isCustomSort = true;
          } else {
            this.setCloudData([...this.dataRepositoryService.cloudTbCollection.values()]);
          }
          this.taskMovementService.allowedTaskMoveData = [...this.dataRepositoryService.cloudTbCollection.values()].filter((f: any) => f.isMovementAllowed)
        }
      })
    );
  }

  /**
   * Method handles subscription for updateColumn event
   */
  selectedColumnEffect() {
    effect(() => {
      if (this.dataRepositoryService.tempSelectedUserColumnPreferences()) {
        const taskBankSelectedPreference = this.dataRepositoryService.tempSelectedUserColumnPreferences()?.[SelectedPreferencesENUM.TASKBANK];
        const newPreferenceId = taskBankSelectedPreference?.id ?? '';
        
        const preferenceChanged = this.currentPreferenceId !== newPreferenceId;
        this.currentPreferenceId = newPreferenceId;
        
        this.visibleColumns = this.taskBankColumnService.getVisibleColumns(ColumnName.PushToCloud, taskBankSelectedPreference?.columnList ?? [])
        
        if (preferenceChanged) {
          this.resetTable();
        }
      } else {
        //set the default column list
        this.visibleColumns = this.taskBankColumnService.getVisibleColumns(ColumnName.PushToCloud);
        if (this.currentPreferenceId !== '') {
          this.currentPreferenceId = '';
          this.resetTable();
        }
      }
    })
  }

  /**
   * Resets the table to apply new column configurations
   */
  resetTable() {
    this.showTable = false;
    setTimeout(() => {
      this.showTable = true;
      // Table (and its virtual Scroller) is recreated here, so reapply the scroll-preservation patch.
      setTimeout(() => {
        this.preserveTableScrollOnRefresh();
        this.applyTaskBankSelectionFilter();
      }, 0);
    }, 0);
  }

  /**
   * PrimeNG virtual-scroll resets scroll to top on data refresh via Scroller.calculateAutoSize
   * and Table._filter -> resetScrollTop. Neutralize both to keep the user's scroll position.
   */
  private preserveTableScrollOnRefresh(): void {
    const table = this.table;
    if (table?.scroller) {
      table.scroller.autoSize = false;
    }
    if (table) {
      table.resetScrollTop = () => {};
    }
  }

  /**
   * This subscription triggers from Map component when certains tasks are selected under drawn polygon
   * & it manages the task selection for task movement
   */
  polygonTaskSelectSubs$(){
    this.taskBankSubscription.push(
      this.dataAccessService.polygonTaskSelection$.subscribe((selectedRows) => {
        if(this.taskInfoService.selectedTabName.toLowerCase()===TBTabName.PushToCloud.toLowerCase()){
          if (selectedRows) {
            this.taskMovementService.taskSelectionFromMapPolygon(selectedRows);
            if (this.taskMovementService.isSelectedAll) {
              this.sortingService.resetingCustomSorting();
            }
            if (this.sortingService.isCustomSortApplied && this.dataRepositoryService.cloudTbCollection.size) {
              this.sortingService.isCustomSort = true;
            }
            else {
              this.setCloudData([...this.dataRepositoryService.cloudTbCollection.values()]);
            }
            this.taskMovementService.allowedTaskMoveData = [...this.dataRepositoryService.cloudTbCollection.values()].filter((f: any) => f.isMovementAllowed)
          }
        }
      })
    );
  }

  /**
   * This subscription triggers from Map component when certains tasks are deselected on delete polygon
   * & it manages the task deselection for task movement list
   */
  polygonTaskDeselectSubs$(){
    this.taskBankSubscription.push(
      this.dataAccessService.polygonTaskDeselection$.subscribe((deSelectRows) => {
        if(this.taskInfoService.selectedTabName.toLowerCase()===TBTabName.PushToCloud.toLowerCase()){
          if (deSelectRows) {
            deSelectRows.forEach((deselectTask:any)=>{
              if(deselectTask.isMovementAllowed){
                this.taskMovementService.handleMovementForTaskDeselection(deselectTask);
                this.taskMovementService.onTaskUnselection(deselectTask.task_id);
              }
            })
            if (this.taskMovementService.isSelectedAll) {
              this.sortingService.resetingCustomSorting();
            }
            if (this.sortingService.isCustomSortApplied && this.dataRepositoryService.cloudTbCollection.size) {
              this.sortingService.isCustomSort = true;
            }
            else {
              this.setCloudData([...this.dataRepositoryService.cloudTbCollection.values()]);
            }
            this.taskMovementService.allowedTaskMoveData = [...this.dataRepositoryService.cloudTbCollection.values()].filter((f: any) => f.isMovementAllowed)
          }
        }
      })
    );
  }

  /**
   * Method runs the effect on task selection
   */
  sortedTaskSelectionEffect() {
    effect(() => {
      if (
        this.sortingService.$sortedTasksForMovement().length
      ) {
        //assign the new sorted data to computed's table data collector
        this.setCloudData([...this.sortingService.$sortedTasksForMovement()])
      }
      else {
        this.setCloudData([...this.dataRepositoryService.cloudTbCollection.values()]);
      }
    });
  } 

  private setCloudData(data: IUPPTasksMap[]): void {
    this.cloudData = this.decorateTaskFilterKeys(data);
    this.applyTaskBankSelectionFilter();
  }

  private applyTaskBankSelectionFilter(): void {
    const selectedKeys = [
      this.selectedTaskFilterServiceCode,
      this.selectedTaskFilterEvent,
    ].filter((key): key is string => !!key);

    this.table?.filter(selectedKeys, this.taskFilterField, 'task-filter-any');
  }

  private decorateTaskFilterKeys(data: IUPPTasksMap[]): IUPPTasksMap[] {
    return data.map((row) => ({
      ...row,
      [this.taskFilterField]: this.taskFilterKeys.filter(
        (key) => Boolean((row as unknown as Record<string, unknown>)[key])
      ),
    }));
  }

  filterData($event: any) {
    this.table.filterGlobal($event?.target?.value ?? '', 'contains')
  }

  /**
   * Method calls the service method for sorting the dataset
   */
  setSelectedTaskSorting(){
    if(!this.sortingService.isCustomSortApplied){
      this.sortingService.setSelectedTaskSorting()
    }
    else{
      this.sortingService.resetingCustomSorting();
    }
  }

  /**
   * Method is called after PrimeNG's built-in sorting is complete
   * @param event sorting parameters 
   */
  onColumnSort(event: any) {
    if(this.dataRepositoryService.cloudTbCollection.size){
      this.sortingService.setFlagsOnSortingEvent(event);
    }
    if (this.taskMovementService.isSelectedAll) {
      this.taskMovementService.selectedOptionsForTaskMove = [...this.cloudData];
    }
  }

  /**
   * Method triggered on select All event, sets the flag and proceeses the task movement 
   * for all tasks
   * @param event select all checkbox click event
   */
  onSelectAllToggle(event:any){
    if (event.checked) {
      this.sortingService.resetingCustomSorting();
    }
    this.taskMovementService.onSelectAllToggle(event);
  }

  /**
   * Single entry point for every table selection change. The one-way [selection]
   * binding keeps selectedOptionsForTaskMove as the previous selection until we
   * overwrite it, so diffing the new selection against it yields the added and
   * removed rows and the phantom anchor unselect on shift+click nets out.
   */
  onSelectionChange(selection: any[]) {
    const previousSelection = (this.taskMovementService.selectedOptionsForTaskMove ?? []).filter(Boolean);
    const previousSelectionIds = new Set(previousSelection.map((task) => task.task_id));
    const normalizedSelection = (selection ?? []).filter(Boolean);
    const currentSelectionIds = new Set(normalizedSelection.map((task) => task.task_id));
    const removedTasks = previousSelection.filter((task) => !currentSelectionIds.has(task.task_id));
    const addedTask = normalizedSelection.find(
      (task) => task?.isMovementAllowed && !previousSelectionIds.has(task.task_id)
    );

    this.taskMovementService.selectedOptionsForTaskMove = normalizedSelection;

    if (removedTasks.length) {
      removedTasks.forEach((task) => this.taskMovementService.onTaskUnselection(task.task_id));
      if (this.taskMovementService.isSelectedAll) {
        this.sortingService.resetingCustomSorting();
        this.taskMovementService.isSelectedAll = false;
      }
    }

    if (addedTask) {
      this.taskMovementService.onTaskSelection(addedTask);
      this.taskMovementService.selectedOptionsForTaskMove =
        this.taskMovementService.selectedOptionsForTaskMove.filter(Boolean);
      if (this.taskMovementService.isSelectedAll) {
        this.taskMovementService.selectedOptionsForTaskMove = [...this.dataRepositoryService.cloudTbCollection.values()];
        this.sortingService.resetingCustomSorting();
      }
    }
  }

  saveColumnWidth(event: any) {
    this.taskBankColumnService.saveColumnWidth(event, this.visibleColumns);
  }
  
  ngOnDestroy(): void {
    if(this.taskBankSubscription && this.taskBankSubscription?.length) {
      this.taskBankSubscription.forEach(item=>item.unsubscribe());
    }
  }
  barcodeClicked(event: any): void {
    window.open(`#/lookup?barcode=` + event.pn_item_id, "_blank");
  }
  /**
   * To open popup for the parked Items
   * @param cloudData selected item details
   */
  openItemDialog(cloudData: IUPPTasksMap) {
    this.itemColumnNames = [
      { field: 'receivername', header: 'Receiver Name' },
      { field: 'pn_item_id', header: 'Barcode' },
      { field: 'itemState', header: 'Item State' },
      { field: 'product_code', header: 'Product Code' },
    ]
    this.itemTitle = `Parked Items for ${cloudData.bl_name}`;
    this.taskid = cloudData.task_id;
    this.visible = true;
  }

  /**
   * To handle the selection of the 
   * table
   * @param $event 
   * @param cloudData 
   */
  handleSelection($event:any,cloudData:IUPPTasksMap) {
    $event.stopPropagation();
    this.taskMovementService.onCloudTaskSelection(cloudData)
    if(this.selectedIds.has(cloudData.task_id)) {
      this.selectedIds.delete(cloudData.task_id);
    } else {
      this.selectedIds.add(cloudData.task_id);
    }
  }

    /**
   * Closes the dialog
   * 
   * @param $event - A boolean indicating whether to close the dialog (true) or not (false).
   */
  closeDialog($event: boolean) {
    this.taskInfoService.closeDialog($event);
  }

  /**\
   * updates the active tab by invoking activeTabSubscription.
   */
  refreshActiveTabData() {
    this.taskInfoService.activeTabSubscription();
  }

  /**
   * get the current dialog type from the updatePopup state in taskInfoService.
   * 
   * @returns The dialog type as a string, or an empty string if not set.
  */
  get DialogType() {
    return this.taskInfoService?.updatePopup?.dialogType ?? '';
  }

  /**
   * Indicates whether the update popup dialog should be shown.
   * 
   * @returns True if the popup should be visible; otherwise false.
   */
  get getShowPopup() {
    return this.taskInfoService?.updatePopup?.showPopup ?? false;
  }

  /**
   * Setter for controlling the visibility of the popup dialog.
   * Updates the `showPopup` property in the `taskInfoService` to reflect
   * the current visibility state of the dialog.
   * 
   * @param value - A boolean indicating whether the popup should be shown (true) or hidden (false).
   */
  set getShowPopup(value: boolean) {
    if (this.taskInfoService?.updatePopup) {
      this.taskInfoService.updatePopup.showPopup = value;
    }
  }

}
